/**
 * Header/AppBar Component
 * Top navigation bar with search, notifications, and user menu
 */

import React, { useState, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  InputBase,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  Chip,
  Tooltip
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from './Sidebar';
import { useQueryClient } from '@tanstack/react-query';
import { useFxSeries, useAlerts, useLocalPrices, useCommodities, useMarkets } from '../../services/queries';
import { useAuth } from '../../context/AuthContext';
import { useThemeMode, MONO, radius } from '../../context/ThemeContext';
import { evaluateAlerts } from '../../utils/alertTriggers';
import { fmtDate } from '../../utils/format';

function Header({ isMobile, onMenuClick, collapsed, onToggleCollapse }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout, isAdmin } = useAuth();
  const { palette } = useThemeMode();
  const navigate = useNavigate();
  const handleGotoPublic = () => navigate('/');

  // Honest FX pill: render only when the feed has data, never a made-up rate.
  const { data: fxSeries } = useFxSeries('ALL');
  const currentFxRate = fxSeries?.[fxSeries.length - 1]?.rate ?? null;
  const previousFxRate = fxSeries?.[fxSeries.length - 2]?.rate ?? null;
  const fxRising = currentFxRate != null && previousFxRate != null && currentFxRate > previousFxRate;

  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/');
  };

  // The bell is a notification center: it counts only alerts whose threshold
  // has actually been crossed by the latest observed prices (premium feature,
  // hidden for admins). An active-but-quiet alert shows nothing.
  const queryClient = useQueryClient();
  const { data: alertsData } = useAlerts({ enabled: !isAdmin });
  const { data: localPrices } = useLocalPrices(undefined, { enabled: !isAdmin });
  const { data: commodities = [] } = useCommodities();
  const { data: markets = [] } = useMarkets();
  const triggered = useMemo(() => evaluateAlerts(alertsData, localPrices), [alertsData, localPrices]);
  const [bellAnchor, setBellAnchor] = useState(null);
  const commodityName = (id) => commodities.find((c) => c.id === id)?.name || id;
  const marketName = (id) => markets.find((m) => m.id === id)?.name || id;
  const handleRefresh = () => queryClient.invalidateQueries();

  const avatarInitials = user?.avatar || (user?.name ? user.name.split(' ').map((p) => p[0]).slice(0, 2).join('') : 'FG');
  const displayName = user?.name || 'Guest';
  const displayEmail = user?.email || '';
  const displayRole = isAdmin ? 'Administrator' : 'Premium User';

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        // Flat full-width bar sitting to the right of the sidebar (NiceAdmin style)
        width: { md: `calc(100% - ${collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH}px)` },
        ml: { md: `${collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH}px` },
        backgroundColor: palette.card,
        color: palette.text,
        borderBottom: `1px solid ${palette.divider}`,
        boxShadow: 'none',
        transition: 'width 0.25s ease, margin-left 0.25s ease',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 64, md: 72 }, px: { xs: 1.5, md: 3 } }}>
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Sidebar toggle - mobile opens the drawer, desktop collapses it */}
          <IconButton
            edge="start"
            color="inherit"
            onClick={isMobile ? onMenuClick : onToggleCollapse}
            sx={{
              color: palette.textSecondary,
              width: 40, height: 40,
              '&:hover': { bgcolor: palette.surfaceHover, color: palette.text },
            }}
            aria-label={isMobile ? 'open navigation' : 'toggle sidebar'}
          >
            <Icon icon="solar:sidebar-minimalistic-outline" width={22} height={22} />
          </IconButton>

          {/* Search - simple pill, no keyboard-shortcut chip */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 260, md: 360 },
            height: 40,
            px: 1.75,
            borderRadius: '999px',
            bgcolor: palette.surfaceHover,
            border: `1px solid transparent`,
            transition: 'all 0.2s ease',
            '&:focus-within': { borderColor: palette.cardBorder, bgcolor: palette.card },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', color: palette.textMuted }}>
              <Icon icon="solar:magnifer-outline" width={18} height={18} />
            </Box>
            <InputBase
              placeholder="Search..."
              inputProps={{ 'aria-label': 'search' }}
              sx={{
                flex: 1,
                color: palette.text,
                fontSize: '0.88rem',
                '& .MuiInputBase-input': {
                  py: 0,
                  '&::placeholder': { color: palette.textMuted, opacity: 1 },
                },
              }}
            />
          </Box>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Public Site - compact outlined button */}
          <Tooltip title="View public site in a new context">
            <Chip
              icon={<Icon icon="solar:arrow-right-up-outline" width={15} height={15} />}
              label="Public site"
              onClick={handleGotoPublic}
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                mr: 0.5,
                height: 30,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: palette.textSecondary,
                bgcolor: 'transparent',
                border: `1px solid ${palette.cardBorder}`,
                borderRadius: '8px',
                '& .MuiChip-icon': { color: palette.textSecondary, ml: 1 },
                '&:hover': { bgcolor: palette.surfaceHover, color: palette.accent, borderColor: palette.accent, '& .MuiChip-icon': { color: palette.accent } },
              }}
            />
          </Tooltip>

          {/* FX Rate chip - a rising NGN/USD rate reads as caution, never a win */}
          {currentFxRate != null && (
            <Tooltip title="Latest NGN/USD close (CBN NFEM)">
              <Chip
                icon={<Icon icon="solar:graph-up-outline" width={16} height={16} />}
                label={`\u20A6${currentFxRate.toLocaleString()}/USD`}
                size="small"
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  fontWeight: 600,
                  fontFamily: MONO,
                  height: 30,
                  borderRadius: '8px',
                  border: `1px solid ${fxRising ? palette.danger : palette.accent}40`,
                  bgcolor: fxRising ? palette.dangerBg : palette.accentBg,
                  color: fxRising ? palette.danger : palette.accent,
                  '& .MuiChip-icon': { color: 'inherit', ml: 1 },
                }}
              />
            </Tooltip>
          )}

          {/* Refresh */}
          <Tooltip title="Refresh data">
            <IconButton aria-label="Refresh data" onClick={handleRefresh} size="small" sx={{ display: { xs: 'none', sm: 'flex' }, color: palette.textSecondary, width: 36, height: 36, '&:hover': { bgcolor: palette.surfaceHover, color: palette.text } }}>
              <Icon icon="solar:refresh-outline" width={18} height={18} />
            </IconButton>
          </Tooltip>

          {/* Notifications - only alerts whose threshold has been crossed */}
          {!isAdmin && (
            <>
              <Tooltip title={triggered.length ? `${triggered.length} triggered alert${triggered.length === 1 ? '' : 's'}` : 'Notifications'}>
                <IconButton
                  aria-label={`Notifications (${triggered.length} triggered alert${triggered.length === 1 ? '' : 's'})`}
                  onClick={(e) => setBellAnchor(e.currentTarget)}
                  size="small"
                  sx={{ color: palette.textSecondary, width: 36, height: 36, '&:hover': { bgcolor: palette.surfaceHover, color: palette.text } }}
                >
                  <Badge
                    badgeContent={triggered.length}
                    sx={{ '& .MuiBadge-badge': { bgcolor: palette.danger, color: '#fff', fontWeight: 700 } }}
                  >
                    <Icon icon="solar:bell-outline" width={18} height={18} />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={bellAnchor}
                open={!!bellAnchor}
                onClose={() => setBellAnchor(null)}
                PaperProps={{
                  sx: {
                    mt: 1,
                    width: 360,
                    borderRadius: `${radius.control}px`,
                    border: `1px solid ${palette.cardBorder}`,
                    boxShadow: palette.shadowLg,
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${palette.divider}` }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>Notifications</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted }}>
                    Alerts whose threshold the market has crossed
                  </Typography>
                </Box>
                {triggered.length === 0 ? (
                  <Box sx={{ px: 2, py: 2.5, maxWidth: 320 }}>
                    <Typography sx={{ fontSize: '0.82rem', color: palette.textSecondary }}>
                      Nothing triggered right now.
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: palette.textMuted, mt: 0.5 }}>
                      When a price crosses one of your thresholds, it will show up here for review.
                    </Typography>
                  </Box>
                ) : (
                  triggered.map(({ alert, observedPrice, marketId, observedDate }) => (
                    <MenuItem
                      key={alert.id}
                      onClick={() => { setBellAnchor(null); navigate('/app/alerts'); }}
                      sx={{ alignItems: 'flex-start', gap: 1.25, py: 1.1, whiteSpace: 'normal' }}
                    >
                      <Box
                        sx={{
                          mt: 0.3,
                          width: 30,
                          height: 30,
                          flexShrink: 0,
                          borderRadius: `${radius.sm}px`,
                          bgcolor: alert.direction === 'below' ? palette.dangerBg : palette.accentBg,
                          color: alert.direction === 'below' ? palette.danger : palette.accent,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon icon={alert.direction === 'below' ? 'ph:trend-down' : 'ph:trend-up'} width={16} height={16} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: palette.text, lineHeight: 1.35 }}>
                          {commodityName(alert.commodityId)} {alert.direction === 'below' ? 'dropped below' : 'rose above'}{' '}
                          <Box component="span" sx={{ fontFamily: MONO }}>₦{alert.threshold.toLocaleString()}</Box>
                        </Typography>
                        <Typography sx={{ fontSize: '0.74rem', color: palette.textSecondary, mt: 0.25 }}>
                          Now <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700 }}>₦{observedPrice.toLocaleString()}</Box>{' '}
                          per {alert.unit === 'kg' ? 'kg' : 'bag'} · {marketName(marketId)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.64rem', color: palette.textMuted, fontStyle: 'italic', mt: 0.25 }}>
                          Observed · {fmtDate(observedDate)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
                <Divider sx={{ borderColor: palette.divider }} />
                <MenuItem
                  onClick={() => { setBellAnchor(null); navigate('/app/alerts'); }}
                  sx={{ justifyContent: 'center', py: 1, color: palette.accent, fontWeight: 600, fontSize: '0.82rem' }}
                >
                  Manage alerts
                </MenuItem>
              </Menu>
            </>
          )}

          {/* User Menu */}
          <IconButton
            edge="end"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: isAdmin ? palette.warning : '#0F2744',
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 700,
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                  boxShadow: `0 0 0 3px ${isAdmin ? palette.warningBg : palette.accentBg}`
                }
              }}
            >
              {avatarInitials}
            </Avatar>
          </IconButton>
        </Box>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 4,
            sx: { mt: 1, minWidth: 220 }
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{displayName}</Typography>
              <Chip label={isAdmin ? 'ADMIN' : 'PREMIUM'} size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.05em', bgcolor: isAdmin ? palette.warningBg : palette.accentBg, color: isAdmin ? palette.warning : palette.accent, ml: 0.5 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
              {displayEmail}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {displayRole}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { handleMenuClose(); navigate('/app/profile'); }}>
            <ListItemIcon><Icon icon="solar:user-outline" width={18} height={18} /></ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => { handleMenuClose(); navigate('/app/settings'); }}>
            <ListItemIcon><Icon icon="solar:settings-outline" width={18} height={18} /></ListItemIcon>
            Settings
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ListItemIcon><Icon icon="solar:logout-3-outline" width={18} height={18} color="#DC2626" /></ListItemIcon>
            Log out
          </MenuItem>
        </Menu>

      </Toolbar>
    </AppBar>
  );
}

export default Header;
